import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Card, CardContent, Typography, CircularProgress, Alert } from "@mui/material";
import MainCard from "ui-component/cards/MainCard";
import { IconCloud, IconCloudOff, IconLogin, IconLogout } from "@tabler/icons-react";

export default function GoogleDrivePage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/backend/drive-oauth/status/", { credentials: "include" });
      if (res.status === 401) {
        window.location.href = "/login";
        return;
      }
      const data = await res.json();
      setStatus(data);
    } catch (e) {
      setError("Erro ao carregar status do Google Drive.");
      setStatus({ connected: false, valid: false, message: "Erro ao verificar conexão." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleConnect = () => {
    window.location.href = "/backend/drive-oauth/start/";
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Desconectar o Google Drive? Os uploads de arquivos de clientes deixarão de funcionar até uma nova conexão.")) return;
    setDisconnecting(true);
    setError(null);
    try {
      const res = await fetch("/backend/drive-oauth/disconnect/", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (res.status === 401) {
        window.location.href = "/login";
        return;
      }
      const data = await res.json();
      if (data.success) {
        await fetchStatus();
      } else {
        setError(data.detail || "Erro ao desconectar.");
      }
    } catch (e) {
      setError("Erro ao desconectar do Google Drive.");
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <MainCard title="Google Drive">
      <Box sx={{ maxWidth: 560 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
            <CircularProgress size={24} />
            <Typography color="text.secondary">Carregando status...</Typography>
          </Box>
        ) : status ? (
          <>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  {status.connected && status.valid ? (
                    <IconCloud size={28} color="green" />
                  ) : status.connected && !status.valid ? (
                    <IconCloud size={28} color="orange" />
                  ) : (
                    <IconCloudOff size={28} color="gray" />
                  )}
                  <Typography variant="subtitle1" fontWeight="medium">
                    Status
                  </Typography>
                </Box>
                <Typography color="text.secondary" variant="body2">
                  {status.message}
                </Typography>
                {status.connected !== undefined && (
                  <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                    {status.connected && status.valid
                      ? "Conexão ativa. Arquivos de clientes são enviados ao Drive."
                      : status.connected && !status.valid
                        ? "Token expirado ou revogado. Reconecte para continuar enviando arquivos."
                        : "Nenhuma conta conectada. Conecte para permitir uploads ao Drive."}
                  </Typography>
                )}
              </CardContent>
            </Card>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                startIcon={<IconLogin size={18} />}
                onClick={handleConnect}
              >
                Conectar ao Google Drive
              </Button>
              {status.connected && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<IconLogout size={18} />}
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                >
                  {disconnecting ? "Desconectando..." : "Desconectar"}
                </Button>
              )}
            </Box>
          </>
        ) : null}
      </Box>
    </MainCard>
  );
}
